import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Tabs,
  Tab,
  Box,
  Checkbox,
  TextField,
  Button,
  Paper,
} from "@material-ui/core";
import DeleteConfirmDialog from './DeleteConfirmDialog'; 

const useStyles = makeStyles((theme) => ({
  tabsRoot: {
    marginBottom: "20px",
    "& .MuiTabs-indicator": {
      height: "2px",
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "14px",
    minWidth: "auto",
    padding: "10px 20px",
    "&.Mui-selected": {
      color: theme.palette.text.contrastText,
    },
    [theme.breakpoints.down("sm")]: {
      padding: "12px 8px",
      fontSize: "13px",
      minWidth: 0,
      flex: 1,
    },
  },
  // Desktop grid layout
  hoursGrid: {
    display: "grid",
    gridTemplateColumns: "100px 1fr 1fr 100px 100px",
    gap: "10px",
    alignItems: "center",
    marginBottom: "10px",
    padding: "8px",
    borderRadius: "4px",

    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  hoursGridHeader: {
    fontWeight: 600,
    marginBottom: "5px",
  },
  dayLabel: {
    fontWeight: 500,
  },
  timeField: {
    "& input": {
      backgroundColor: "white",
      padding: "6px 10px",
      fontSize: "13px",
    },
  },
  timeFieldClosed: {
    "& input": {
      backgroundColor: "grey",
      padding: "6px 10px",
      fontSize: "13px",
    },
  },
  intervalNote: {
    fontSize: "11px",
    color: theme.palette.grey.main,
    fontStyle: "italic",
    marginTop: "4px",
  },
  // Mobile card layout
  mobileContainer: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  dayCard: {
    backgroundColor: theme.palette.tertiary.paper,
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "12px",
    border: "1px solid #e0e0e0",
  },
  dayCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  dayCardTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: theme.palette.grey.main,
  },
  timeRange: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    "@media (max-width: 480px)": {
      flexDirection: "column",
      gap: "8px",
      alignItems: "stretch",
    },
  },
  timeFieldMobile: {
    flex: 1,
    "& input": {
      padding: "12px",
      fontSize: "16px",
      backgroundColor: "white",
      color: "#000",
      borderRadius: "8px",
    },
    "@media (max-width: 480px)": {
      width: "100%",
    },
  },
  timeSeparator: {
    fontWeight: 500,
    color: "#666",
    "@media (max-width: 480px)": {
      display: "none",
    },
  },
  checkedBox: {
    color: theme.palette.text.primary, // unchecked color
  },
  closedLabel: {
    fontSize: "14px",
    color: "#999",
    fontStyle: "italic",
    textAlign: "center",
    padding: "8px",
  },
  contactCard: {
    textAlign: "center",
    padding: "40px 20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    color: theme.palette.text.contrastText,
  },
  activateServiceButton: {
    background: theme.palette.tertiary.main,
  },
  warningPaper: {
    padding: theme.spacing(2.5),
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeaa7",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1.25),
  },
  warningTitle: {
    color: "#856404",
    marginBottom: theme.spacing(1.25),
  },
  warningText: {
    color: "#856404",
    marginBottom: theme.spacing(2.5),
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(1.25),
    "& > *": {
      marginRight: theme.spacing(1.25),
    },
    "& > *:last-child": {
      marginRight: 0,
    },
  },
  deactivateButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
  rootCheckedBox: {
    color: theme.palette.text.primary, // unchecked color
    "&$checked": {
      color: theme.palette.primary.main, // checked color (blue by default)
    },
  },
  checked: {},
  mobileOpen24hrs: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: '8px',
    marginRight: '8px',
  }
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const HoursManagement = React.forwardRef(
  (
    {
      initialHoursData,
      activeServices,
      availableServices,
      onHoursChange,
      onDeleteService,
    },
    ref
  ) => {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState(0);
    const [hoursData, setHoursData] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [activated, setActivated] = useState({});
    const [showDeactivateWarning, setShowDeactivateWarning] = useState(null);

    const activeServiceIdSet = React.useMemo(() => {
      if (!activeServices || !activeServices.length) return new Set();
      return new Set(activeServices.map((s) => s.id));
    }, [activeServices]);

    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    useEffect(() => {
      if (!availableServices || !availableServices.length) return;

      setHoursData((prev) => {
        const next = { ...prev };
        availableServices.forEach(({ id }) => {
          if (initialHoursData?.[id]) {
            // merge, don't replace
            next[id] = {
              ...(next[id] || {}),
              ...initialHoursData[id],
            };
            for (let day = 0; day <= 6; day++) {
              if (!next[id][day]) {
                next[id][day] = {
                  intervals: [
                    {
                      start_time: "09:00",
                      end_time: "17:00",
                      crosses_midnight: false,
                    },
                  ],
                  is_closed_all_day: false,
                  is_24_hours: false,
                };
              }
            }
          } else {
            // only initialize if we don't already have local edits
            if (!next[id]) {
              next[id] = {};
              for (let day = 0; day <= 6; day++) {
                next[id][day] = { intervals: [], is_closed_all_day: true, is_24_hours: false };
              }
            }
          }
        });
        return next;
      });
    }, [initialHoursData, availableServices]);

    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    const handleDayToggle = (serviceId, dayIndex, isClosed) => {
      setHoursData((prevData) => {
        const newData = { ...prevData };
        if (!newData[serviceId]) newData[serviceId] = {};

        newData[serviceId][dayIndex] = isClosed
          ? { is_closed_all_day: true, is_24_hours: false, intervals: [] }
          : {
              is_closed_all_day: false,
              is_24_hours: false,
              intervals: [
                {
                  start_time: "09:00",
                  end_time: "17:00",
                  crosses_midnight: false,
                },
              ],
            };

        return newData;
      });

      setHasChanges(true);
      onHoursChange?.(true);
    };

    const is24HourInterval = (interval) => {
      return interval.start_time === "00:00" && interval.end_time === "23:59";
    }

    const handleTimeChange = (serviceId, dayIndex, field, value) => {
      setHoursData((prevData) => {
        const newData = { ...prevData };
        if (!newData[serviceId]) newData[serviceId] = {};
        if (!newData[serviceId][dayIndex]) {
          newData[serviceId][dayIndex] = {
            intervals: [
              {
                start_time: "09:00",
                end_time: "17:00",
                crosses_midnight: false,
              },
            ],
            is_closed_all_day: false,
            is_24_hours: false
          };
        }

        const dayData = newData[serviceId][dayIndex];
        if (!dayData.intervals?.length) {
          dayData.intervals = [
            { start_time: "09:00", end_time: "17:00", crosses_midnight: false },
          ];
        }

        dayData.intervals[0] = { ...dayData.intervals[0], [field]: value };
        dayData.intervals[0].crosses_midnight =
          dayData.intervals[0].start_time > dayData.intervals[0].end_time;

          // uncheck 24 hrs if time changes
          if (!is24HourInterval(dayData.intervals[0])) {
            dayData.is_24_hours = false;
          }

        return newData;
      });

      setHasChanges(true);
      onHoursChange?.(true);
    };

    const handle24hrsToggle = (serviceId, dayIndex, checked) => {
      setHoursData((prev) => {
        const newData = { ...prev };
        const dayData =
          newData[serviceId]?.[dayIndex] ??
          (newData[serviceId][dayIndex] = {
            intervals: [],
            is_closed_all_day: false,
            is_24_hours: false,
          });
    
        if (checked) {
          dayData.is_closed_all_day = false;
          dayData.is_24_hours = true;
          dayData.intervals = [
            {
              start_time: "00:00",
              end_time: "23:59",
              crosses_midnight: false,
            },
          ];
        } else {
          dayData.is_24_hours = false;
        }
    
        return newData;
      });
    
      setHasChanges(true);
      onHoursChange?.(true);
    };

    const getDayData = (serviceId, dayIndex) =>
      hoursData[serviceId]?.[dayIndex] || {
        intervals: [],
        is_closed_all_day: true,
        is_24_hours: false,
      };

    const getPrimaryInterval = (serviceId, dayIndex) => {
      const dayData = getDayData(serviceId, dayIndex);
      return (
        dayData.intervals?.[0] || {
          start_time: "09:00",
          end_time: "17:00",
          crosses_midnight: false,
        }
      );
    };

    const renderServiceHours = ({ serviceId, serviceLabel }) => {
      const isServiceAvailable = activeServiceIdSet.has(serviceId);
      // Only change the return when service is NOT available
      if (!isServiceAvailable && !activated[serviceId]) {
        return (
          <Paper className={classes.contactCard} elevation={0}>
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              {serviceLabel} not activated for this tenant
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "20px" }}>
              This tenant doesn’t have the {serviceLabel} service activated.
            </Typography>
            <Button
              className={classes.activateServiceButton}
              onClick={(e) => {
                // show normal hours form and default to all CLOSED
                setActivated((prev) => ({ ...prev, [serviceId]: true }));
                setHoursData((prev) => {
                  const next = { ...prev };
                  if (!next[serviceId]) next[serviceId] = {};
                  for (let day = 0; day <= 6; day++) {
                    next[serviceId][day] = {
                      intervals: [],
                      is_closed_all_day: true,
                      is_24_hours: false,
                    };
                  }
                  return next;
                });
                setHasChanges(true);
                onHoursChange?.(true);

                // submit the wrapping form with the newly activated hours
                const form = e.currentTarget.closest("form");
                // defer one tick so React applies state before the submit handler reads it
                setTimeout(() => form?.requestSubmit?.(), 0);
              }}
            >
              Activate Service
            </Button>
          </Paper>
        );
      }

      const actionsBar = (
        <DeleteConfirmDialog
          title={`Deactivate ${serviceLabel} Service`}
          message={`You are about to deactivate the ${serviceLabel} service. This will remove all hours configuration for this service. This action cannot be undone.`}
          showWarning={showDeactivateWarning === serviceId}
          isLoading={false}  // or add loading state if needed
          onTrigger={() => setShowDeactivateWarning(serviceId)}
          onConfirm={async () => {
            try {
              await onDeleteService?.(serviceId);
              setHoursData((prev) => {
                const next = { ...prev };
                if (!next[serviceId]) next[serviceId] = {};
                for (let day = 0; day <= 6; day++) {
                  next[serviceId][day] = {
                    intervals: [],
                    is_closed_all_day: true,
                    is_24_hours: false
                  };
                }
                return next;
              });
              setActivated((prev) => ({ ...prev, [serviceId]: false }));
              setHasChanges(false);
              onHoursChange?.(false);
              setShowDeactivateWarning(null);
            } catch (e) {
              console.error("Local delete handling error:", e);
            }
          }}
          onCancel={() => setShowDeactivateWarning(null)}
          triggerLabel="Deactivate Service"
          confirmLabel="Deactivate"
        />
      );

      const desktopView = (
        <div>
          <div className={`${classes.hoursGrid} ${classes.hoursGridHeader}`}>
            <div>Day</div>
            <div>Open</div>
            <div>Close</div>
            <div>Closed</div>
            <div>24 hrs</div>
          </div>

          {dayNames.map((dayName, dayIndex) => {
            const dayData = getDayData(serviceId, dayIndex);
            const primaryInterval = getPrimaryInterval(serviceId, dayIndex);
            const isClosed = dayData.is_closed_all_day;
            const hasMultipleIntervals = dayData.intervals?.length > 1;

            return (
              <div key={dayIndex}>
                <div className={classes.hoursGrid}>
                  <div className={classes.dayLabel}>
                    {dayName.substring(0, 3)}
                  </div>
                  <TextField
                    type="time"
                    size="small"
                    variant="outlined"
                    className={
                      isClosed ? classes.timeFieldClosed : classes.timeField
                    }
                    value={isClosed ? "" : primaryInterval.start_time}
                    disabled={isClosed}
                    onChange={(e) =>
                      handleTimeChange(
                        serviceId,
                        dayIndex,
                        "start_time",
                        e.target.value
                      )
                    }
                  />
                  <TextField
                    type="time"
                    size="small"
                    variant="outlined"
                    className={
                      isClosed ? classes.timeFieldClosed : classes.timeField
                    }
                    value={isClosed ? "" : primaryInterval.end_time}
                    disabled={isClosed}
                    onChange={(e) =>
                      handleTimeChange(
                        serviceId,
                        dayIndex,
                        "end_time",
                        e.target.value
                      )
                    }
                  />
                  <Checkbox
                    checked={isClosed}
                    onChange={(e) =>
                      handleDayToggle(serviceId, dayIndex, e.target.checked)
                    }
                    classes={{
                      root: classes.rootCheckedBox,
                      checked: classes.checked,
                    }}
                    size="small"
                  />
                  <Checkbox
                  checked={dayData.is_24_hours}
                  disabled={isClosed}
                  classes={{
                    root: classes.rootCheckedBox,
                    checked: classes.checked,
                  }}
                  size="small"
                  onChange={(e) => handle24hrsToggle(serviceId, dayIndex, e.target.checked)}
                  />
                  
                </div>
                {hasMultipleIntervals && !isClosed && (
                  <div className={classes.intervalNote}>
                    * This day has {dayData.intervals.length} time intervals.
                    Only the first is shown.
                  </div>
                )}
                {primaryInterval.crosses_midnight && !isClosed && (
                  <div className={classes.intervalNote}>* Crosses midnight</div>
                )}
              </div>
            );
          })}
        </div>
      );

      const mobileView = (
        <div className={classes.mobileContainer}>
          {dayNames.map((dayName, dayIndex) => {
            const dayData = getDayData(serviceId, dayIndex);
            const primaryInterval = getPrimaryInterval(serviceId, dayIndex);
            const isClosed = dayData.is_closed_all_day;
            const hasMultipleIntervals = dayData.intervals?.length > 1;

            return (
              <Paper key={dayIndex} className={classes.dayCard} elevation={0}>
                <div className={classes.dayCardHeader}>
                  <Typography className={classes.dayCardTitle}>
                    {dayName}
                  </Typography>
                  <Checkbox
                    checked={isClosed}
                    onChange={(e) =>
                      handleDayToggle(serviceId, dayIndex, e.target.checked)
                    }
                    className={classes.checkedBox}
                    color="primary"
                  />
                </div>

                {isClosed ? (
                  <Typography className={classes.closedLabel}>
                    Closed
                  </Typography>
                ) : (
                  <>
                  <div className={classes.timeRange}>
                    <TextField
                      type="time"
                      variant="outlined"
                      className={classes.timeFieldMobile}
                      value={primaryInterval.start_time}
                      onChange={(e) =>
                        handleTimeChange(
                          serviceId,
                          dayIndex,
                          "start_time",
                          e.target.value
                        )
                      }
                    />
                    <span className={classes.timeSeparator}>to</span>
                    <TextField
                      type="time"
                      variant="outlined"
                      className={classes.timeFieldMobile}
                      value={primaryInterval.end_time}
                      onChange={(e) =>
                        handleTimeChange(
                          serviceId,
                          dayIndex,
                          "end_time",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className={classes.mobileOpen24hrs}>
                    <Typography>Open 24 hrs</Typography>
                  <Checkbox
                  checked={dayData.is_24_hours}
                  disabled={isClosed}
                  classes={{
                    root: classes.rootCheckedBox,
                    checked: classes.checked,
                  }}
                  onChange={(e) => handle24hrsToggle(serviceId, dayIndex, e.target.checked)}
                  />
                  </div>
                  </>
                )}

                {hasMultipleIntervals && !isClosed && (
                  <Typography
                    className={classes.intervalNote}
                    style={{ marginTop: "8px" }}
                  >
                    * This day has {dayData.intervals.length} time intervals.
                    Only the first is shown.
                  </Typography>
                )}
                {primaryInterval.crosses_midnight && !isClosed && (
                  <Typography
                    className={classes.intervalNote}
                    style={{ marginTop: "8px" }}
                  >
                    * Crosses midnight
                  </Typography>
                )}
              </Paper>
            );
          })}
        </div>
      );

      return (
        <>
          {desktopView}
          {mobileView}
          {actionsBar}
        </>
      );
    };

    React.useImperativeHandle(ref, () => ({
      getHoursData: () => {
        const availableHoursData = {};
        const activeSet = new Set([
          ...Array.from(activeServiceIdSet),
          ...Object.keys(activated).filter((k) => activated[k]),
        ]);

        availableServices.forEach(({ id }) => {
          if (activeSet.has(id)) {
            availableHoursData[id] = hoursData[id];
          }
        });

        return availableHoursData;
      },
      hasChanges: () => hasChanges,
      resetChanges: () => setHasChanges(false),
    }));

    return (
      <div style={{ padding: "10px" }}>
        <Typography variant="h6"  style={{ marginBottom: "8px" }}>
          Hours of Operation
        </Typography>
        {/* Note */}
        <Typography variant="h6"  style={{ marginBottom: "15px" }}>
          *Note: To show tenant sites on the map, latitude and longitude are currently required. If a tenant site has latitude and longitude but no hours set, it will display on the map as <strong>Coming Soon</strong>.*
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          className={classes.tabsRoot}
          variant="fullWidth"
        >
          {availableServices?.map(({ name, id }) => (
            <Tab key={id} label={name} className={classes.tab} />
          ))}
        </Tabs>

        {availableServices?.map(({ name, id }, index) => (
          <TabPanel key={id} value={activeTab} index={index}>
            {renderServiceHours({ serviceId: id, serviceLabel: name })}
          </TabPanel>
        ))}
      </div>
    );
  }
);

export default HoursManagement;
